import { User } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Mixpanel } from 'mixpanel-react-native';
import { Platform } from 'react-native';
import { getBundledAppVersion } from '../lib/appVersion';
import { logger } from '../lib/logger';

const MIXPANEL_TOKEN = Constants.expoConfig?.extra?.mixpanelToken || process.env.EXPO_PUBLIC_MIXPANEL_TOKEN;

/** Production app origin — Mixpanel "Current URL" is always this + path (no gamma/localhost). */
const MIXPANEL_WEB_ORIGIN = 'https://app.openstudiojazz.com';

/** `pathWithQuery` = pathname + optional ?search, e.g. `/courses/foo` or `/skillpath/x?courseSlug=y` */
function mixpanelUrlFields(pathWithQuery: string): { urlPath: string; currentUrl: string } {
  const path = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
  const q = path.indexOf('?');
  const pathnameOnly = q === -1 ? path : path.slice(0, q);
  return {
    urlPath: pathnameOnly,
    currentUrl: `${MIXPANEL_WEB_ORIGIN}${path}`,
  };
}

/** Human-readable surface labels for search events. */
export type MixpanelSearchScope = 'Global' | 'Courses Hub' | 'In Course';

/** Include a property only when it has a real value (never "undefined" strings). */
function withOptional(
  props: Record<string, any>,
  name: string,
  value: string | null | undefined,
): Record<string, any> {
  if (typeof value === 'string' && value.trim().length > 0) {
    props[name] = value.trim();
  }
  return props;
}

const MIXPANEL_NAMES= {
  pages: {
    root: 'Root',
    course: 'Course',
    collection: 'Collection',
    lesson: 'Lesson',
    skillPath: 'Skill Path',
  },
  events: {
    openedApp: 'Opened App',
    viewedPage: 'Viewed Page',
    signedIn: 'Signed In',
    signedOut: 'Signed Out',
    clickedWorksheetDownload: 'Clicked Worksheet Download',
    clickedElement: 'Clicked Element',
    performedSearch: 'Performed Search',
    clickedSearchResult: 'Clicked Search Result',
    downloadedVideo: 'Downloaded Video',
    deletedDownload: 'Deleted Download',
    playedOfflineVideo: 'Played Offline Video',
  },
  properties: {
    elementName: 'Element Name',
    chapterTitle: 'Chapter Title',
    chapterBeginSeconds: 'Chapter Begin Seconds',
    videoSlug: 'Video Slug',
    courseSlug: 'Course Slug',
    skillPathSlug: 'Skill Path Slug',
    collectionSlug: 'Collection Slug',
    lessonSlug: 'Lesson Slug',
    courseTitle: 'Course Title',
    collectionTitle: 'Collection Title',
    lessonTitle: 'Lesson Title',
    skillPathTitle: 'Skill Path Title',
    searchScope: 'Search Scope',
    searchTerm: 'Search Term',
    searchResultCount: 'Search Result Count',
    resultType: 'Result Type',
    resultSlug: 'Result Slug',
    resultTitle: 'Result Title',
    resultPosition: 'Result Position',
    currentUrl: 'Current URL',
    urlPath: 'URL Path',
    link: 'Link',
    pageName: 'Page Name',
    platform: 'Platform',
    appVersion: 'App Version',
    spUserId: 'Supabase User ID',
    signInMethod: 'Sign In Method',
    announcementId: 'Announcement ID',
    announcementTitle: 'Announcement Title',
    announcementLink: 'Announcement Link',
    hubCtaId: 'Hub CTA ID',
    hubCtaTarget: 'Hub CTA Target',
    hubCtaTitle: 'Hub CTA Title',
    hubCtaLink: 'Hub CTA Link',
    downloadBytes: 'Download Bytes',
    downloadRendition: 'Download Rendition',
    videoDurationSeconds: 'Video Duration Seconds',
    // Subscription traits — super properties (on every event) + people properties.
    onTrial: 'On Trial',
    trialEndAt: 'Trial End At',
    membershipTier: 'Membership Tier',
    membershipSource: 'Membership Source',
    subscriptionStatus: 'Subscription Status',
    billingInterval: 'Billing Interval',
    joinDate: 'Join Date',
  }
}

/** Subset of lib/offline DownloadRecord the download events report. */
export type MixpanelDownloadRecord = {
  courseSlug: string;
  lessonSlug: string;
  videoSlug: string | null;
  courseTitle: string;
  lessonTitle: string;
  bytesDownloaded: number;
  rendition: string;
  durationSeconds: number;
};

/** Subscription-derived traits attached to events and the people profile. */
export type MixpanelSubscriptionTraits = {
  onTrial: boolean;
  trialEndAt: string | null;
  membershipTier: string;
  /** 'apple' | 'web' | 'unknown' — who bills this member (readMembershipSource). */
  membershipSource: string;
  subscriptionStatus: string | null;
  billingInterval: string | null;
  /** Supabase auth created_at (WP registration for post-bridge users). */
  joinDate: string | null;
};

class MixpanelService {
  private mixpanel: any = null;
  private isInitialized = false;
  private currentUserId: string | null = null;
  // Add a Promise to guarantee that initialization happens only once and safely:
  private initializationPromise: Promise<void> | null = null; 

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    // Protects against multiple simultaneous calls (race conditions)
    if (this.initializationPromise) {
        return this.initializationPromise;
    }

    // Create the initialization Promise and store it
    this.initializationPromise = new Promise(async (resolve, reject) => {
        try {
            this.mixpanel = new Mixpanel(MIXPANEL_TOKEN, false);
            await this.mixpanel.init();
            
            __DEV__ && logger.debug('Mixpanel initialized in JavaScript mode (normal for Expo)');
            this.isInitialized = true;
            resolve();
        } catch (error) {
            logger.error('Failed to initialize Mixpanel:', error);
            // In case of error, reset the promise to null for a retry
            this.initializationPromise = null; 
            reject(error);
        }
    });

    // Return the new/existing promise
    return this.initializationPromise;
  }

  async identifyUser(email: string): Promise<void> {
    // Ensure that initialization has finished
    if (!this.mixpanel || !this.isInitialized) {
      await this.initialize();
    }

    try {
      // Get current distinct_id
      const currentDistinctId = await this.mixpanel.getDistinctId();
      const isCurrentIdIdentifiedEmail = currentDistinctId.includes('@');
      
      // If already identified with the same email, skip
      if (this.currentUserId === email && currentDistinctId === email) {
        return;
      }
      
      // If trying to identify with different email, reset first
      if (isCurrentIdIdentifiedEmail && currentDistinctId !== email) {
        __DEV__ && logger.debug('Switching user, resetting Mixpanel session...');
        await this.mixpanel.reset();
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      await this.mixpanel.identify(email);
      this.currentUserId = email;

      // Add People attributes to ensure the merging of the email identity
      // This is crucial for Mixpanel to correctly associate the email with the distinct ID.
      // (`getPeople()` lazily creates the People instance; the old `.people`
      // property check was always false before the first getPeople() call.)
      this.mixpanel.getPeople().set({
        '$email': email,
      });
      
    } catch (error) {
      logger.error('Failed to identify user in Mixpanel:', error);
    }
  }

  getDistinctId(): string | null {
    // Use identified user email if available (most reliable)
    if (this.currentUserId) {
      return this.currentUserId;
    }

    if (!this.mixpanel || !this.isInitialized) {
      return null;
    }

    try {
      const distinctId = this.mixpanel.getDistinctId();
      
      // If it's a Promise, we can't await it here
      if (distinctId && typeof distinctId === 'object' && 'then' in distinctId) {
        return null;
      }
      
      if (typeof distinctId === 'string' && distinctId.length > 0) {
        return distinctId;
      }
    } catch (error) {
      logger.error('Failed to get distinct ID from Mixpanel SDK:', error);
    }
    
    return null;
  }

  /**
   * Track an event with auto-identify
   */
  async track(eventName: string, properties: Record<string, any> = {}, user?: User | null): Promise<void> {
    // Ensure that initialization has finished before continuing
    if (!this.mixpanel || !this.isInitialized) {
      await this.initialize();
    }

    try {
      // Auto-identify user if email is provided and different from current user
      if (user?.email && this.currentUserId !== user.email) {
        await this.identifyUser(user.email);
      }

      // Add common properties to all events
      const enrichedProperties = {
        ...properties,
        [MIXPANEL_NAMES.properties.spUserId]: user?.id,
        [MIXPANEL_NAMES.properties.platform]: Platform.OS,
        [MIXPANEL_NAMES.properties.appVersion]: getBundledAppVersion(),
        'user_id': user?.email || this.currentUserId,
      };

      await this.mixpanel.track(eventName, enrichedProperties);
      
      if (__DEV__) {
        const finalDistinctId = await this.mixpanel.getDistinctId();
        __DEV__ && logger.debug('🔍 Mixpanel Event Tracked:', {
          event: eventName,
          properties: enrichedProperties,
          distinctId: finalDistinctId,
          expectedDistinctId: this.currentUserId || 'anonymous'
        });
      }
    } catch (error) {
      logger.error('Failed to track event in Mixpanel:', error);
    }
  }

  /** Courses list at `/courses` — URL path is the primary dimension. */
  async trackCoursesRootVisit(
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(MIXPANEL_NAMES.events.viewedPage, {
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.root,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    }, user);
  }

  async trackCourseLandingVisit(
    courseSlug: string,
    user: User | null,
    pathWithQuery: string,
    courseTitle?: string | null,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, any> = {
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.course,
      [MIXPANEL_NAMES.properties.courseSlug]: courseSlug,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
    };
    withOptional(props, MIXPANEL_NAMES.properties.courseTitle, courseTitle);
    await this.track(MIXPANEL_NAMES.events.viewedPage, props, user);
  }

  async trackCollectionsVisit(
    courseSlug: string,
    collectionSlug: string,
    user: User | null,
    pathWithQuery: string,
    courseTitle?: string | null,
    collectionTitle?: string | null,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, any> = {
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.collection,
      [MIXPANEL_NAMES.properties.courseSlug]: courseSlug,
      [MIXPANEL_NAMES.properties.collectionSlug]: collectionSlug,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    };
    withOptional(props, MIXPANEL_NAMES.properties.courseTitle, courseTitle);
    withOptional(props, MIXPANEL_NAMES.properties.collectionTitle, collectionTitle);
    await this.track(MIXPANEL_NAMES.events.viewedPage, props, user);
  }

  async trackLessonDetailVisit(
    courseSlug: string,
    lessonSlug: string,
    user: User | null,
    pathWithQuery: string,
    courseTitle?: string | null,
    lessonTitle?: string | null,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, any> = {
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.lesson,
      [MIXPANEL_NAMES.properties.courseSlug]: courseSlug,
      [MIXPANEL_NAMES.properties.lessonSlug]: lessonSlug,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    };
    withOptional(props, MIXPANEL_NAMES.properties.courseTitle, courseTitle);
    withOptional(props, MIXPANEL_NAMES.properties.lessonTitle, lessonTitle);
    await this.track(MIXPANEL_NAMES.events.viewedPage, props, user);
  }

  async trackSkillPathwayVisit(
    skillPathSlug: string,
    referrerCourseSlug: string | undefined,
    user: User | null,
    pathWithQuery: string,
    skillPathTitle?: string | null,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, string> = {
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.skillPath,
      [MIXPANEL_NAMES.properties.skillPathSlug]: skillPathSlug,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    };
    if (referrerCourseSlug && referrerCourseSlug.length > 0) {
      props[MIXPANEL_NAMES.properties.courseSlug] = referrerCourseSlug;
    }
    withOptional(props, MIXPANEL_NAMES.properties.skillPathTitle, skillPathTitle);
    await this.track(MIXPANEL_NAMES.events.viewedPage, props, user);
  }

  async trackSearchPerformed(
    scope: MixpanelSearchScope,
    searchTerm: string,
    resultCount: number,
    user: User | null,
    pathWithQuery: string,
    courseSlug?: string | null,
    courseTitle?: string | null,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, any> = {
      [MIXPANEL_NAMES.properties.searchScope]: scope,
      [MIXPANEL_NAMES.properties.searchTerm]: searchTerm,
      [MIXPANEL_NAMES.properties.searchResultCount]: resultCount,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    };
    withOptional(props, MIXPANEL_NAMES.properties.courseSlug, courseSlug);
    withOptional(props, MIXPANEL_NAMES.properties.courseTitle, courseTitle);
    await this.track(MIXPANEL_NAMES.events.performedSearch, props, user);
  }

  async trackSearchResultClick(
    scope: MixpanelSearchScope,
    searchTerm: string,
    result: {
      type: string;
      slug?: string | null;
      title?: string | null;
      courseSlug?: string | null;
      position: number;
    },
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    const props: Record<string, any> = {
      [MIXPANEL_NAMES.properties.searchScope]: scope,
      [MIXPANEL_NAMES.properties.searchTerm]: searchTerm,
      [MIXPANEL_NAMES.properties.resultType]: result.type,
      [MIXPANEL_NAMES.properties.resultPosition]: result.position,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    };
    withOptional(props, MIXPANEL_NAMES.properties.resultSlug, result.slug);
    withOptional(props, MIXPANEL_NAMES.properties.resultTitle, result.title);
    withOptional(props, MIXPANEL_NAMES.properties.courseSlug, result.courseSlug);
    await this.track(MIXPANEL_NAMES.events.clickedSearchResult, props, user);
  }

  async trackChapterLinkClick(
    courseSlug: string,
    lessonSlug: string,
    videoSlug: string,
    chapterTitle: string,
    chapterBeginSeconds: number,
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(MIXPANEL_NAMES.events.clickedElement, {
      [MIXPANEL_NAMES.properties.elementName]: 'Chapter Link',
      [MIXPANEL_NAMES.properties.pageName]: MIXPANEL_NAMES.pages.lesson,
      [MIXPANEL_NAMES.properties.courseSlug]: courseSlug,
      [MIXPANEL_NAMES.properties.lessonSlug]: lessonSlug,
      [MIXPANEL_NAMES.properties.videoSlug]: videoSlug,
      [MIXPANEL_NAMES.properties.chapterTitle]: chapterTitle,
      [MIXPANEL_NAMES.properties.chapterBeginSeconds]: chapterBeginSeconds,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    }, user);
  }

  async trackAnnouncementClick(
    announcementId: number | string,
    announcementTitle: string,
    announcementLink: string,
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(MIXPANEL_NAMES.events.clickedElement, {
      [MIXPANEL_NAMES.properties.elementName]: 'Announcement',
      [MIXPANEL_NAMES.properties.announcementId]: announcementId,
      [MIXPANEL_NAMES.properties.announcementTitle]: announcementTitle,
      [MIXPANEL_NAMES.properties.announcementLink]: announcementLink,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    }, user);
  }

  async trackPdfMaterialClick(
    url: string,
    courseSlug: string,
    lessonSlug: string,
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(MIXPANEL_NAMES.events.clickedWorksheetDownload, {
      [MIXPANEL_NAMES.properties.link]: url,
      [MIXPANEL_NAMES.properties.courseSlug]: courseSlug,
      [MIXPANEL_NAMES.properties.lessonSlug]: lessonSlug,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    }, user);
  }


  // ---- Offline downloads (native only) ----------------------------------

  private downloadProps(record: MixpanelDownloadRecord): Record<string, any> {
    return withOptional(
      {
        [MIXPANEL_NAMES.properties.courseSlug]: record.courseSlug,
        [MIXPANEL_NAMES.properties.lessonSlug]: record.lessonSlug,
        [MIXPANEL_NAMES.properties.courseTitle]: record.courseTitle,
        [MIXPANEL_NAMES.properties.lessonTitle]: record.lessonTitle,
        [MIXPANEL_NAMES.properties.downloadBytes]: record.bytesDownloaded,
        [MIXPANEL_NAMES.properties.downloadRendition]: record.rendition,
        [MIXPANEL_NAMES.properties.videoDurationSeconds]: Math.round(record.durationSeconds || 0),
      },
      MIXPANEL_NAMES.properties.videoSlug,
      record.videoSlug,
    );
  }

  async trackVideoDownloaded(record: MixpanelDownloadRecord): Promise<void> {
    await this.track(MIXPANEL_NAMES.events.downloadedVideo, this.downloadProps(record));
  }

  async trackDownloadDeleted(record: MixpanelDownloadRecord): Promise<void> {
    await this.track(MIXPANEL_NAMES.events.deletedDownload, this.downloadProps(record));
  }

  async trackOfflineVideoPlayed(
    record: MixpanelDownloadRecord,
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(
      MIXPANEL_NAMES.events.playedOfflineVideo,
      {
        ...this.downloadProps(record),
        [MIXPANEL_NAMES.properties.urlPath]: urlPath,
        [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
      },
      user,
    );
  }

  async trackAppOpened(user: User | null): Promise<void> {
    await this.track(MIXPANEL_NAMES.events.openedApp, {
    }, user);
  }

  async trackUserSignIn(user: User, method: string = 'email'): Promise<void> {
    await this.track(MIXPANEL_NAMES.events.signedIn, {
      [MIXPANEL_NAMES.properties.signInMethod]: method
    }, user);
  }

  async trackUserSignOut(user: User): Promise<void> {
    await this.track(MIXPANEL_NAMES.events.signedOut, {}, user);
  }

  /** Hub CTA banner click (e.g. the Jazz Piano First Steps trial CTA). */
  async trackHubCtaClick(
    cta: { id: number; target: string; title: string; link: string },
    user: User | null,
    pathWithQuery: string,
  ): Promise<void> {
    const { urlPath, currentUrl } = mixpanelUrlFields(pathWithQuery);
    await this.track(MIXPANEL_NAMES.events.clickedElement, {
      [MIXPANEL_NAMES.properties.elementName]: 'Hub CTA',
      [MIXPANEL_NAMES.properties.hubCtaId]: cta.id,
      [MIXPANEL_NAMES.properties.hubCtaTarget]: cta.target,
      [MIXPANEL_NAMES.properties.hubCtaTitle]: cta.title,
      [MIXPANEL_NAMES.properties.hubCtaLink]: cta.link,
      [MIXPANEL_NAMES.properties.urlPath]: urlPath,
      [MIXPANEL_NAMES.properties.currentUrl]: currentUrl,
    }, user);
  }

  /**
   * Attach subscription traits: super properties so every event (Viewed Page
   * etc.) carries "On Trial" / "Membership Tier" as of that moment, and people
   * properties so cohorts ("trialing users") can be built on the profile.
   * "Join Date" is set once. Super properties are cleared by reset() on sign-out.
   */
  async setSubscriptionTraits(
    traits: MixpanelSubscriptionTraits,
    user: User | null,
  ): Promise<void> {
    if (!this.mixpanel || !this.isInitialized) {
      await this.initialize();
    }
    try {
      if (user?.email && this.currentUserId !== user.email) {
        await this.identifyUser(user.email);
      }

      const P = MIXPANEL_NAMES.properties;
      const shared: Record<string, any> = {
        [P.onTrial]: traits.onTrial,
        [P.membershipTier]: traits.membershipTier,
        [P.membershipSource]: traits.membershipSource,
        [P.subscriptionStatus]: traits.subscriptionStatus ?? 'none',
      };
      withOptional(shared, P.billingInterval, traits.billingInterval);
      withOptional(shared, P.trialEndAt, traits.trialEndAt);

      this.mixpanel.registerSuperProperties(shared);

      if (user?.email) {
        const people = this.mixpanel.getPeople();
        people.set(shared);
        if (traits.joinDate) {
          people.setOnce({ [P.joinDate]: traits.joinDate });
        }
      }
    } catch (error) {
      logger.error('Failed to set Mixpanel subscription traits:', error);
    }
  }

  async flush(): Promise<void> {
    if (!this.mixpanel || !this.isInitialized) return;
    try {
      await this.mixpanel.flush();
    } catch (error) {
      logger.error('Failed to flush Mixpanel queue:', error);
    }
  }

  async reset(): Promise<void> {
    if (!this.mixpanel || !this.isInitialized) return;

    try {
      await this.mixpanel.reset();
      this.currentUserId = null;
      __DEV__ && logger.debug('Mixpanel session reset');
    } catch (error) {
      logger.error('Failed to reset Mixpanel session:', error);
    }
  }
}

export const mixpanelService = new MixpanelService();
export default mixpanelService;